import { DashboardConfig, DashboardLayout, DashboardPage, DashboardRow } from '../../../types/gotypes'
import { createPlaceholderCell } from '../../../utils/dashboardBuilderLayout'

/**
 * System template library (#971 Phase 1). Bayes Price managed, available to
 * every customer — no persistence, no API call, just a hardcoded
 * `DashboardConfig` per template. Phase 2 adds company/personal templates
 * backed by real dashboards; these are deliberately structure-only, same as
 * that phase's "structure and widget config shells only" rule, so today's
 * system templates already behave the way a promoted template will.
 *
 * `build(job)` returns a **fresh** config every call — fresh page ids in
 * particular, so loading the same template twice (or loading it, discarding,
 * loading again) never collides ids the way pasting a tab must avoid (#967).
 */

export interface SystemTemplate {
  id: string
  name: string
  description: string
  tags: string[]
  build: (job: string) => DashboardConfig
}

function row(...cells: ReturnType<typeof createPlaceholderCell>[]): DashboardRow {
  return { cells }
}

function layout(...rows: DashboardRow[]): DashboardLayout {
  return { rows }
}

function page(name: string, l: DashboardLayout): DashboardPage {
  return { id: crypto.randomUUID(), name, layout: l }
}

function cfg(name: string, description: string, pages: DashboardPage[]): DashboardConfig {
  return {
    name,
    description,
    layout: pages[0]?.layout || { rows: [] },
    pages,
  }
}

function titleHero(job: string) {
  return createPlaceholderCell('title_hero', job)
}
function genAi(job: string) {
  return createPlaceholderCell('multi_table_analysis', job)
}
function chart(job: string) {
  return createPlaceholderCell('chart', job)
}
function table(job: string) {
  return createPlaceholderCell('table', job)
}
function kpi(job: string) {
  return createPlaceholderCell('kpi_card', job)
}
function text(job: string) {
  return createPlaceholderCell('text', job)
}

const textOnlyThreeColumns: SystemTemplate = {
  id: 'text-only-3col',
  name: 'Text only — 3 columns',
  description: 'Title hero + a 3-column Gen AI insight layout (mirrors an exec report structure).',
  tags: ['exec report', '3 columns', 'text-only'],
  build: (job) => cfg('Text only — 3 columns', '', [
    page('Page 1', layout(
      row(titleHero(job)),
      row(genAi(job), genAi(job), genAi(job)),
    )),
  ]),
}

const execReportSingleTab: SystemTemplate = {
  id: 'exec-report-single',
  name: 'Executive report — single tab',
  description: 'Title + 3 Gen AI insight cards + table references.',
  tags: ['exec report', '3 columns', 'chart-led'],
  build: (job) => cfg('Executive report', '', [
    page('Page 1', layout(
      row(titleHero(job)),
      row(genAi(job), genAi(job), genAi(job)),
      row(table(job)),
    )),
  ]),
}

const execReportMultiTab: SystemTemplate = {
  id: 'exec-report-multi',
  name: 'Executive report — multi-tab',
  description: 'Same structure as the single-tab exec report, pre-built across 5 tabs.',
  tags: ['exec report', '3 columns', 'multi-tab', 'chart-led'],
  build: (job) => cfg('Executive report', '', Array.from({ length: 5 }, (_, i) => page(`Page ${i + 1}`, layout(
    row(titleHero(job)),
    row(genAi(job), genAi(job), genAi(job)),
    row(table(job)),
  )))),
}

const brandFunnel: SystemTemplate = {
  id: 'brand-funnel',
  name: 'Brand funnel',
  description: 'KPI row + 3 charts (awareness / consideration / preference), each with a Gen AI insight beneath.',
  tags: ['brand tracking', '3 columns', 'chart-led', 'mixed'],
  build: (job) => cfg('Brand funnel', '', [
    page('Page 1', layout(
      row(kpi(job), kpi(job), kpi(job)),
      row(chart(job), chart(job), chart(job)),
      row(genAi(job), genAi(job), genAi(job)),
    )),
  ]),
}

const dataDeepDive: SystemTemplate = {
  id: 'data-deep-dive',
  name: 'Data deep dive',
  description: 'Chart, table and a Gen AI insight, stacked in a single column.',
  tags: ['single metric', 'chart-led', 'mixed'],
  build: (job) => cfg('Data deep dive', '', [
    page('Page 1', layout(
      row(chart(job)),
      row(table(job)),
      row(genAi(job)),
    )),
  ]),
}

const chartAndInsight: SystemTemplate = {
  id: '1-chart-1-insight',
  name: '1 chart + 1 insight',
  description: 'A chart and a Gen AI text card, side by side.',
  tags: ['single metric', 'chart-led'],
  build: (job) => cfg('1 chart + 1 insight', '', [
    page('Page 1', layout(
      row(chart(job), genAi(job)),
    )),
  ]),
}

const kpiDashboard: SystemTemplate = {
  id: 'kpi-dashboard',
  name: 'KPI dashboard',
  description: 'A grid of KPI cards with a summary text box.',
  tags: ['single metric', '3 columns'],
  build: (job) => cfg('KPI dashboard', '', [
    page('Page 1', layout(
      row(kpi(job), kpi(job), kpi(job)),
      row(text(job)),
    )),
  ]),
}

const singleMetric: SystemTemplate = {
  id: 'single-metric',
  name: 'Single metric',
  description: 'A large KPI card with a supporting chart and text.',
  tags: ['single metric', 'text-only'],
  build: (job) => cfg('Single metric', '', [
    page('Page 1', layout(
      row(kpi(job)),
      row(chart(job)),
      row(text(job)),
    )),
  ]),
}

export const SYSTEM_TEMPLATES: SystemTemplate[] = [
  textOnlyThreeColumns,
  execReportSingleTab,
  execReportMultiTab,
  brandFunnel,
  dataDeepDive,
  chartAndInsight,
  kpiDashboard,
  singleMetric,
]

export function getSystemTemplate(id: string): SystemTemplate | undefined {
  return SYSTEM_TEMPLATES.find(t => t.id === id)
}

/** Every tag across every template, in first-seen order — used to build the filter chip row. */
export function allTemplateTags(): string[] {
  const seen = new Set<string>()
  const tags: string[] = []
  for (const t of SYSTEM_TEMPLATES) {
    for (const tag of t.tags) {
      if (!seen.has(tag)) {
        seen.add(tag)
        tags.push(tag)
      }
    }
  }
  return tags
}
